import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AtSign, FileText, Lock, MessageCircle, Paperclip, Reply, Send, X, Zap } from 'lucide-react';
import { ClipboardEvent, KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useUser } from '../../app/session';
import { toast } from '../../components/toasts';
import { Button, Textarea } from '../../components/ui';
import { errorMessage, get, post } from '../../lib/api';
import { normalizePhoto } from '../../lib/image';
import { contactLabel } from '../../lib/format';
import { useOperators } from '../../lib/queries';
import { fillQuickReply, matchQuickReplies, QuickReply, slashQuery } from '../../lib/quick-replies';
import { AttendanceDetails, Message } from '../../lib/types';
import { AudioRecorder } from './AudioRecorder';
import { messageSnippet } from './MessageList';
import { QuickReplyMenu } from './QuickReplyMenu';

const MAX_FILE_BYTES = 16 * 1024 * 1024;

interface Pending {
  id: string;
  file: File;
  /** Prévia de imagem (blob:). */
  preview: string | null;
}

const readBase64 = (file: File) =>
  new Promise<string>((ok, fail) => {
    const reader = new FileReader();
    reader.onload = () => ok(String(reader.result).replace(/^data:[^;]*;base64,/, ''));
    reader.onerror = () => fail(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Resposta ao cliente (responsável ou quem ajuda) ou anotação interna (qualquer operador; nunca vai ao
 * cliente). Os dois modos têm cor, faixa e botão diferentes para não confundir. Quem ajuda começa em
 * anotação interna e confirma a primeira mensagem ao cliente.
 * Anexos pelo clipe ou colando uma imagem (Ctrl+V); o texto vira a legenda do primeiro anexo.
 * Enter envia; Shift+Enter quebra a linha.
 */
/** Tela estreita (celular): textos de ajuda mais curtos. */
function useNarrow(): boolean {
  const query = '(max-width: 767px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}

export function Composer({
  attendance,
  canReply,
  startAsNote = false,
  voice = false,
  replyTo = null,
  onClearReply,
}: {
  attendance: AttendanceDetails;
  canReply: boolean;
  startAsNote?: boolean;
  /** Atendimento de ligação: só anotações internas (não há WhatsApp na conversa). */
  voice?: boolean;
  /** Mensagem sendo respondida: vai citada no WhatsApp do cliente. */
  replyTo?: Message | null;
  onClearReply?: () => void;
}) {
  const user = useUser();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'reply' | 'note'>(canReply && !startAsNote ? 'reply' : 'note');
  // Quem ajuda confirma a primeira mensagem ao cliente (evita mandar ao cliente o que era para a equipe).
  const [replyConfirmed, setReplyConfirmed] = useState(!startAsNote);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [picker, setPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState<Pending[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const operators = useOperators().data?.filter((o) => o.id !== user.id) ?? [];
  const effectiveMode = canReply ? mode : 'note';
  const narrow = useNarrow();
  // Responder uma mensagem: a resposta vai ao cliente, e o cursor já fica no campo.
  useEffect(() => {
    if (!replyTo) return;
    if (canReply) {
      // Anexos separados para a anotação interna não podem seguir para o cliente.
      if (mode === 'note' && filesRef.current.length) {
        filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        setFiles([]);
        toast('Anexos da anotação interna descartados: a resposta vai ao cliente', 'info');
      }
      setMode('reply');
    }
    textRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só quando o operador escolhe responder
  }, [replyTo, canReply]);
  const replyId = effectiveMode === 'reply' && replyTo ? replyTo.id : undefined;

  // Respostas rápidas: "/" no início ou depois de um espaço abre a lista (filtrada pelo que vem depois).
  const textRef = useRef<HTMLTextAreaElement>(null);
  // Como no WhatsApp: o campo cresce com o texto até o limite (max-h no CSS) e daí passa a rolar.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [text]);
  const quickReplies = useQuery({ queryKey: ['quick-replies'], queryFn: () => get<QuickReply[]>('/quick-replies'), staleTime: 5 * 60_000 }).data ?? [];
  const [slash, setSlash] = useState<{ start: number; query: string } | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const slashItems = slash ? matchQuickReplies(quickReplies, slash.query, attendance.teamId).slice(0, 8) : [];
  const updateSlash = (value: string, cursor: number) => {
    const next = slashQuery(value, cursor);
    setSlash(next);
    if (next?.query !== slash?.query) setSlashIndex(0);
  };
  /** Troca o "/atalho" digitado pelo texto da resposta, com as variáveis preenchidas. */
  const pickQuickReply = (reply: QuickReply) => {
    if (!slash) return;
    const filled = fillQuickReply(reply.body, { contactName: attendance.contactName, operatorName: user.name, ticketCode: attendance.ticketCode });
    const end = slash.start + 1 + slash.query.length;
    const next = text.slice(0, slash.start) + filled + text.slice(end);
    setText(next.slice(0, 4096));
    setSlash(null);
    const caret = Math.min(slash.start + filled.length, 4096);
    requestAnimationFrame(() => {
      textRef.current?.focus();
      textRef.current?.setSelectionRange(caret, caret);
    });
  };
  /** Botão do raio (útil no celular): põe a "/" no cursor e abre a lista. */
  const openQuickReplies = () => {
    const el = textRef.current;
    const cursor = el?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const insert = before && !/\s$/.test(before) ? ' /' : '/';
    const next = before + insert + text.slice(cursor);
    setText(next);
    const caret = cursor + insert.length;
    updateSlash(next, caret);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  };

  // Libera as prévias só ao sair da tela (as atuais ficam em uso enquanto a lista muda).
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => () => filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), []);

  const addFiles = (list: FileList | File[]) => {
    const next: Pending[] = [];
    for (const file of Array.from(list)) {
      if (file.size > MAX_FILE_BYTES) {
        toast(`${file.name || 'Arquivo'}: passa de 16 MB`, 'error');
        continue;
      }
      next.push({ id: crypto.randomUUID(), file, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });
    }
    setFiles((f) => [...f, ...next].slice(0, 10));
  };

  /**
   * Troca o destino (cliente ↔ anotação interna). Os anexos escolhidos são descartados: um arquivo
   * separado para a equipe nunca pode ir ao cliente só porque o destino mudou antes de enviar.
   */
  const switchMode = (next: 'reply' | 'note') => {
    if (next === effectiveMode) return;
    if (files.length) {
      if (!confirm('Trocar o destino descarta os anexos selecionados. Continuar?')) return;
      files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      setFiles([]);
    }
    setMode(next);
  };

  const removeFile = (id: string) =>
    setFiles((list) => {
      const gone = list.find((f) => f.id === id);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return list.filter((f) => f.id !== id);
    });

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = Array.from(e.clipboardData.files);
    if (!pasted.length) return;
    e.preventDefault();
    // Imagem colada vem sem nome útil: dá um nome com data e hora.
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    addFiles(pasted.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `imagem-${stamp}${i ? `-${i}` : ''}.${(f.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg')}`, { type: f.type }))));
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && !files.length) || sending) return;
    if (effectiveMode === 'reply' && !replyConfirmed) {
      if (!confirm(`Esta mensagem vai para o CLIENTE (${contactLabel(attendance)}) no WhatsApp, com o seu nome. Enviar?`)) return;
      setReplyConfirmed(true);
    }
    setSending(true);
    try {
      if (effectiveMode === 'reply' && files.length) {
        for (const [i, p] of files.entries()) {
          // Foto da câmera: endireitada antes de enviar (o WhatsApp ignora a rotação do arquivo).
          const file = await normalizePhoto(p.file);
          await post(`/conversations/${attendance.conversationId}/attachments`, {
            fileName: file.name || 'arquivo',
            mimeType: file.type || 'application/octet-stream',
            dataBase64: await readBase64(file),
            caption: i === 0 && body ? body : undefined,
            replyToMessageId: i === 0 ? replyId : undefined,
          });
        }
        files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        setFiles([]);
        onClearReply?.();
      } else if (effectiveMode === 'reply') {
        await post(`/conversations/${attendance.conversationId}/messages`, { text: body, replyToMessageId: replyId });
        onClearReply?.();
      } else if (files.length) {
        // Anotação interna com arquivos: o texto e as menções vão no primeiro.
        for (const [i, p] of files.entries()) {
          const file = await normalizePhoto(p.file);
          await post(`/conversations/${attendance.conversationId}/notes/attachments`, {
            fileName: file.name || 'arquivo',
            mimeType: file.type || 'application/octet-stream',
            dataBase64: await readBase64(file),
            caption: i === 0 && body ? body : undefined,
            mentions: i === 0 ? mentions : undefined,
          });
        }
        files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        setFiles([]);
        setMentions([]);
      } else {
        await post(`/conversations/${attendance.conversationId}/notes`, { text: body, mentions });
        setMentions([]);
      }
      setText('');
      void qc.invalidateQueries({ queryKey: ['messages', attendance.conversationId] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSending(false);
    }
  };

  /** Áudio gravado: vai na hora, como mensagem de voz ao cliente ou como anotação interna. */
  const sendAudio = async (file: File) => {
    const note = effectiveMode === 'note';
    if (!note && !replyConfirmed) {
      if (!confirm(`Este áudio vai para o CLIENTE (${contactLabel(attendance)}) no WhatsApp. Enviar?`)) return;
      setReplyConfirmed(true);
    }
    try {
      const data = { fileName: file.name, mimeType: file.type, dataBase64: await readBase64(file) };
      if (note) {
        await post(`/conversations/${attendance.conversationId}/notes/attachments`, data);
      } else {
        await post(`/conversations/${attendance.conversationId}/attachments`, { ...data, replyToMessageId: replyId });
        onClearReply?.();
      }
      void qc.invalidateQueries({ queryKey: ['messages', attendance.conversationId] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slash && !e.nativeEvent.isComposing) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlash(null);
        return;
      }
      if (slashItems.length) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const step = e.key === 'ArrowDown' ? 1 : -1;
          setSlashIndex((i) => (i + step + slashItems.length) % slashItems.length);
          return;
        }
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
          e.preventDefault();
          pickQuickReply(slashItems[Math.min(slashIndex, slashItems.length - 1)]);
          return;
        }
      }
    }
    // Celular: Enter quebra a linha (como no WhatsApp); envia pelo botão.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !narrow) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div
      className={clsx('border-t-4 px-4 py-3', effectiveMode === 'note' ? 'border-warn bg-bubble-note/40' : 'border-accent bg-panel')}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        addFiles(e.dataTransfer.files);
      }}
    >
      <div className="mb-2 flex items-center gap-1 text-xs">
        {!voice && (
        <button
          disabled={!canReply}
          onClick={() => switchMode('reply')}
          className={clsx(
            'flex items-center gap-1 rounded-md border px-2.5 py-1 font-semibold',
            effectiveMode === 'reply' ? 'border-accent bg-accent text-accent-fg' : 'border-border text-fg-soft hover:bg-muted',
            !canReply && 'cursor-not-allowed opacity-50',
          )}
          title={canReply ? 'Mensagem para o cliente no WhatsApp' : 'Assuma o atendimento (ou entre para ajudar) para responder ao cliente'}
        >
          <MessageCircle className="size-3.5" /> Para o cliente
        </button>
        )}
        <button
          onClick={() => switchMode('note')}
          className={clsx(
            'flex items-center gap-1 rounded-md border px-2.5 py-1 font-semibold',
            effectiveMode === 'note' ? 'border-warn bg-warn text-white' : 'border-border text-fg-soft hover:bg-muted',
          )}
          title="Só a equipe vê; nunca vai ao cliente"
        >
          <Lock className="size-3.5" /> Anotação interna
        </button>
        {effectiveMode === 'note' && (
          <div className="relative ml-auto">
            <button className="flex items-center gap-1 rounded-md px-2 py-1 text-fg-soft hover:bg-muted" onClick={() => setPicker((p) => !p)}>
              <AtSign className="size-3.5" /> Mencionar {mentions.length > 0 && `(${mentions.length})`}
            </button>
            {picker && (
              <div className="absolute right-0 bottom-full z-20 mb-1 w-56 rounded-md border border-border bg-panel py-1 shadow-lg" onMouseLeave={() => setPicker(false)}>
                {operators.length === 0 && <div className="px-3 py-2 text-fg-soft">Nenhum outro operador.</div>}
                {operators.map((o) => (
                  <label key={o.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-muted">
                    <input type="checkbox" checked={mentions.includes(o.id)} onChange={(e) => setMentions((m) => (e.target.checked ? [...m, o.id] : m.filter((x) => x !== o.id)))} />
                    {o.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {effectiveMode === 'reply' ? (
        <div className="mb-2 flex items-center gap-1.5 rounded-md bg-accent/10 px-2.5 py-1.5 text-xs font-medium text-accent">
          <MessageCircle className="size-3.5 shrink-0" />
          <span>
            Vai para o <b>cliente</b> ({contactLabel(attendance)}) no WhatsApp, assinada como {user.name.split(/\s+/)[0]}.
          </span>
        </div>
      ) : (
        <div className="mb-2 flex items-center gap-1.5 rounded-md bg-warn/15 px-2.5 py-1.5 text-xs font-medium text-warn">
          <Lock className="size-3.5 shrink-0" />
          <span>
            {voice ? (
              <>Atendimento por telefone: registre aqui o que foi tratado. Só a equipe vê; vai no registro interno do chamado.</>
            ) : (
              <>Anotação interna: só a equipe vê. O cliente <b>não</b> recebe.</>
            )}
          </span>
        </div>
      )}
      {replyId && replyTo && (
        <div className="mb-2 flex items-start gap-2 rounded-md border-l-4 border-accent bg-muted px-2.5 py-1.5 text-xs">
          <Reply className="mt-0.5 size-3.5 shrink-0 text-accent" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-accent">Respondendo a {replyTo.direction === 'inbound' ? 'mensagem do cliente' : 'mensagem nossa'}</div>
            <div className="line-clamp-2 text-fg-soft">{messageSnippet(replyTo)}</div>
          </div>
          <button className="rounded p-0.5 text-fg-soft hover:bg-panel" onClick={() => onClearReply?.()} aria-label="Cancelar resposta" title="Cancelar resposta">
            <X className="size-3.5" />
          </button>
        </div>
      )}
      {effectiveMode === 'note' && mentions.length > 0 && (
        <div className="mb-1 text-xs text-fg-soft">
          Avisar: {operators.filter((o) => mentions.includes(o.id)).map((o) => o.name).join(', ')}
        </div>
      )}
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((p) => (
            <div key={p.id} className="relative flex items-center gap-2 rounded-md border border-border bg-muted p-1.5 pr-7 text-xs">
              {p.preview ? <img src={p.preview} alt="" className="size-12 rounded object-cover" /> : <FileText className="size-5 text-fg-soft" />}
              <span className="max-w-40 truncate">{p.file.name}</span>
              <button className="absolute top-1 right-1 rounded p-0.5 text-fg-soft hover:bg-panel" onClick={() => removeFile(p.id)} aria-label="Remover anexo">
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Coluna estreita (e celular): o texto ocupa a linha de cima e os botões ficam embaixo. */}
      <div className="flex flex-wrap items-end gap-1 @xl/chat:flex-nowrap @xl/chat:gap-2">
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Button
          variant="ghost"
          onClick={() => fileInput.current?.click()}
          aria-label="Anexar arquivo"
          title={effectiveMode === 'note' ? 'Anexar arquivo à anotação interna: só a equipe vê (ou cole uma imagem no campo)' : 'Anexar arquivo (ou cole uma imagem no campo)'}
        >
          <Paperclip className="size-4" />
        </Button>
        <AudioRecorder disabled={sending} onRecorded={sendAudio} />

        <Button variant="ghost" onClick={openQuickReplies} aria-label="Respostas rápidas" title="Respostas rápidas (ou digite / no texto)">
          <Zap className="size-4" />
        </Button>
        <div className="relative min-w-0 flex-1 @max-xl/chat:order-first @max-xl/chat:basis-full">
          {slash && (
            <QuickReplyMenu
              items={slashItems}
              index={Math.min(slashIndex, Math.max(0, slashItems.length - 1))}
              query={slash.query}
              hasAny={matchQuickReplies(quickReplies, '', attendance.teamId).length > 0}
              onPick={pickQuickReply}
              onHover={setSlashIndex}
            />
          )}
        <Textarea
          ref={textRef}
          rows={1}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            updateSlash(e.target.value, e.target.selectionStart);
          }}
          onKeyDown={onKey}
          onKeyUp={(e) => {
            // Setas laterais e Home/End mudam o cursor sem mudar o texto.
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) updateSlash(e.currentTarget.value, e.currentTarget.selectionStart);
          }}
          onClick={(e) => updateSlash(e.currentTarget.value, e.currentTarget.selectionStart)}
          onBlur={() => setSlash(null)}
          onPaste={onPaste}
          maxLength={4096}
          placeholder={
            effectiveMode === 'reply'
              ? files.length
                ? 'Legenda (opcional)'
                : narrow
                  ? 'Mensagem para o cliente'
                  : 'Mensagem para o cliente (Enter envia, Shift+Enter quebra linha, Ctrl+V cola imagem)'
              : files.length
                ? 'Texto da nota (opcional)'
                : 'Nota interna: só a equipe vê, nunca vai ao cliente'
          }
          className="block max-h-36 w-full md:max-h-52"
        />
        </div>
        <Button
          variant={effectiveMode === 'note' ? 'secondary' : 'primary'}
          className={clsx('shrink-0 @max-xl/chat:ml-auto', effectiveMode === 'note' && 'border-warn text-warn')}
          onClick={() => void send()}
          loading={sending}
          disabled={!text.trim() && !files.length}
          aria-label={effectiveMode === 'note' ? 'Salvar anotação interna' : 'Enviar ao cliente'}
        >
          {effectiveMode === 'note' ? <Lock className="size-4" /> : <Send className="size-4" />}
          <span className="hidden sm:inline">{effectiveMode === 'note' ? 'Salvar nota' : 'Enviar ao cliente'}</span>
        </Button>
      </div>
    </div>
  );
}
